import {
	getFormProps,
	getInputProps,
	getSelectProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useFetcher } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../button/button'
import { Field } from '../forms/forms'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import { useIsFetching } from '~/utils/misc'
import { subscribeToNewsletterSchema } from '~/types/subscribe-to-newsletter'
import Select from '../select/Select'
import {
	acceptedCountries,
	getCountryCodeByLanguage,
	getCountryName,
	useClientLang,
} from '~/utils/i18n.utils'
import { HoneypotInputs } from 'remix-utils/honeypot/react'

export default function SubscribeToNewsletter() {
	const fetcher = useFetcher()
	const isFetching = useIsFetching(fetcher)
	const { t } = useTranslation()
	const lng = useClientLang()
	const [form, fields] = useForm({
		id: 'newsletter-subscription',
		constraint: getZodConstraint(subscribeToNewsletterSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: subscribeToNewsletterSchema })
		},
		defaultValue: {
			country: getCountryCodeByLanguage(lng.lngShort),
		},
		shouldRevalidate: 'onInput',
	})

	return (
		<section className="col-span-12 flex flex-col gap-y-4 rounded-lg border border-border bg-card md:mb-8">
			{fetcher.data && fetcher.state === 'idle' ? (
				<div className="flex flex-col items-center gap-4 p-8 text-center">
					<Icon className="text-secondary" name="check-badge" size="xxxxl" />
					<Text as="h3">{t('newsletter_thanks_for_signup')}</Text>
				</div>
			) : (
				<div className="grid grid-cols-12 items-center gap-y-4 p-4 md:gap-8 md:p-8">
					<div className="col-span-12 flex justify-center md:col-span-4">
						<Icon className="text-secondary" name="envelope" size="xxxxl" />
					</div>
					<div className="col-span-12 flex flex-col gap-y-4 md:col-span-8">
						<div className="text-center md:text-start">
							<Text as="h3">
								{t('newsletter_be_informed_first')}
								<span className="text-secondary">?</span>
							</Text>
							<Text>{t('newsletter_subscribe_to_newsletter')}</Text>
						</div>
						<fetcher.Form
							method="POST"
							className="flex w-full flex-col gap-4 md:flex-row"
							encType="multipart/form-data"
							{...getFormProps(form)}
							action="/api/user/subscribe-to-newsletter"
						>
							<HoneypotInputs />
							<Field
								className="mb-0 w-full md:w-1/2"
								labelProps={{ children: t('form_label_email'), hidden: true }}
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									autoComplete: 'current-email',
									placeholder: t('form_placeholder_email'),
								}}
								errors={fields.email.errors}
							/>
							<Select
								labelProps={{}}
								inputProps={{
									...getSelectProps(fields.country),
								}}
								options={acceptedCountries.map(x => {
									return {
										value: x.code,
										label: getCountryName(x.code, lng.lngShort) ?? x.language,
									}
								})}
							/>
							<Button variant="secondary" type="submit" disabled={isFetching}>
								{t('newsletter_keep_me_updated')}
							</Button>
						</fetcher.Form>
					</div>
				</div>
			)}
		</section>
	)
}
